<template>
  <div
    v-if="canPerformOperationForCurrentService('train_model')"
    class="owal-model__container"
  >
    <owal-model-details
      v-if="showModelDetails"
      :label="selectedModel.label"
      :top-one="selectedModel.top1"
      :top-five="selectedModel.top5"
      :domain-u-u-i-d="domainUUID"
      :parsed-label="parseLabel(selectedModel.label)"
      @close="handleClose"
      :modelKey="selectedModel.key"
    />
    <owal-panel
      class="owal-model__panel"
      title="Manage Faces System"
      :bottom-buttons="bottomButtons"
      @button-click="handleButton"
    >
      <span class="owal-warn">WARNING: </span>
      <span>the features on this page could have unexpected consequences and result in detection errors. Only make changes if you're certain about what you're doing.
        <span style="font-weight: bold; ">{{ selectedModelDetails }}</span>
      </span>
      <!-- Begin model selector panel -->
      <div class="owal-model__available-model-container">
        <!-- Headers -->
        <div class="owal-model__model-card">
          <div
            class="owal-model__model-card-column owal-model__model-label owal-model__label-column"
            @click="sortCategory = 'date'"
          >
            Date Created
          </div>
          <div
            class="owal-model__model-card-column owal-model__model-label owal-model__secondary-column"
            @click="sortCategory = 'top1'"
          >
            Top 1 Accuracy
          </div>
          <div
            class="owal-model__model-card-column owal-model__model-label owal-model__secondary-column"
            @click="sortCategory = 'top5'"
          >
            Top 5 Accuracy
          </div>
          <!-- These currently ensure that the columns are equally spaced, but could be better  -->
          <i
            class="info circle icon"
            style="visibility: hidden;"
          />
          <owal-banner-button
            title="Button"
            accessory-type="none"
            style="visibility: hidden;"
            identifier="missing-button"
          />
        </div>
        <div
          v-for="(model, index) in availableModels"
          :key="model.label"
          class="owal-model__model-description"
        >
          <div
            :id="modelSelected(model.label) ? 'selected' : ''"
            class="owal-model__model-card"
          >
            <div class="owal-model__model-card-column owal-model__label-column">
              <div>{{ `${parseLabel(model.label)}` }}</div>
            </div>
            <div class="owal-model__model-card-column owal-model__secondary-column">
              <div>{{ `${model.top1}%` }}</div>
            </div>
            <div class="owal-model__model-card-column owal-model__secondary-column">
              <div>
                {{ `${model.top5}%` }}
              </div>
            </div>
            <i
              class="large info circle icon tooltip"
              style="color:black;"
              @click="viewModelDetails(index)"
            />
            <owal-banner-button
              title="Load"
              :style="modelSelected(model.label) ? 'visibility: hidden;' : ''"
              accessory-type="none"
              identifier="load"
              @click="setModel(index)"
            />
          </div>
        </div>
      </div>
      <!-- End model selector panel -->
    </owal-panel>
  </div>
</template>

<script>
import { OwalBannerButton } from '@/libraries/owal-ui/'
import OwalPanel from '@/libraries/owal-ui/panels/OwalPanel'
import OwalModelDetails from '@/components/faces-system/ModelDetails'
import { trainModel, setNewFacesModel } from '@/libraries/api/faces'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'FacesSystemManagement',
  components: {
    OwalBannerButton,
    OwalPanel,
    OwalModelDetails,
  },
  data () {
    return {
      selectedModel: undefined,
      showModelDetails: undefined,
      sortCategory: 'date',
    }
  },
  computed: {
    ...mapGetters({
      domainUUID: 'currentDomainUUID',
      canPerformOperationForCurrentService: 'entitlements/canPerformOperationForCurrentService',
      availableModels: 'trainingTool/sortedModels',
      currentModel: 'trainingTool/currentModel',
    }),
    selectedModelDetails () {
      if (this.currentModel) {
        const date = moment.utc(this.currentModel.label, 'MMM DD, YYYY HH:mm:ss').local().format('MMMM Do, YYYY HH:mm:ss')
        return `The current model was created on ${date}. `
      } else {
        return 'No current model is selected or found.'
      }
    },
    bottomButtons () {
      let defaultButtons = [{
        title: 'New Training Process',
        identifier: 'train',
      }]

      return defaultButtons
    },
  },
  mounted () {
    this.getModelsForCurrentDomain()
    this.getCurrentModel()
  },
  methods: {
    ...mapActions({
      getModelsForCurrentDomain: 'trainingTool/getModelsForCurrentDomain',
      getCurrentModel: 'trainingTool/getCurrentModel',
    }),
    handleButton (identifier) {
      if (identifier === 'train') {
        this.trainFacesModel()
      }
    },
    sortFunctionForCategory () {
      const sortByDate = function (a, b) {
        const aUnix = moment(a.label, 'YYYY-MM-DD-HH-mm-ss').utc().valueOf()
        const bUnix = moment(b.label, 'YYYY-MM-DD-HH-mm-ss').utc().valueOf()
        if (aUnix > bUnix) {
          return -1
        } else if (aUnix < bUnix) {
          return 1
        } else {
          return 0
        }
      }

      if (this.sortCategory === 'date') {
        return sortByDate
      } else if (this.sortCategory === 'top1') {
        return function (a, b) {
          const aUnix = moment(a.top1_accuracy, 'YYYY-MM-DD-HH-mm-ss').utc().valueOf()
          const bUnix = moment(b.top1_accuracy, 'YYYY-MM-DD-HH-mm-ss').utc().valueOf()
          if (aUnix < bUnix) {
            return -1
          } else if (aUnix > bUnix) {
            return 1
          } else {
            return 0
          }
        }
      } else if (this.sortCategory === 'top5') {
        return function (a, b) {
          const aUnix = moment(a.top5_accuracy, 'YYYY-MM-DD-HH-mm-ss').utc().valueOf()
          const bUnix = moment(b.top5_accuracy, 'YYYY-MM-DD-HH-mm-ss').utc().valueOf()
          if (aUnix < bUnix) {
            return -1
          } else if (aUnix > bUnix) {
            return 1
          } else {
            return 0
          }
        }
      } else {
        return sortByDate
      }
    },
    trainFacesModel () {
      const sure = confirm('Are you sure?')
      if (sure) {
        trainModel(this.domainUUID).then(response => {
          alert(response.data.message)
        })
          .catch(error => {
            alert(error.data.message)
          })
      }
    },
    viewModelDetails (modelIndex) {
      this.selectedModel = this.availableModels[modelIndex]
      this.showModelDetails = true
    },
    handleClose () {
      this.showModelDetails = false
    },
    toFixed (value) {
      const floatValue = parseFloat(value)
      const percent = floatValue * 100
      return Math.round(percent * 100) / 100
    },
    parseLabel (label) {
      const parsed = moment.utc(label, 'MMM DD, YYYY HH:mm:ss').local().format('MMMM Do, YYYY HH:mm:ss')
      return parsed
    },
    selectModel (index) {
      this.selectedModel = this.availableModels[index]
    },
    modelSelected (label) {
      if (this.currentModel.label === label) {
        return true
      } else return false
    },
    setModel (modelIndex) {
      this.selectedModel = this.availableModels[modelIndex]
      setNewFacesModel(this.domainUUID, this.selectedModel.key).then(response => {
        alert('The model was succesfully loaded. Please wait a moment and then refresh the page.')
      }).catch(err => {
        console.error('error: ', err)
        alert('There was a problem loading the model. Please contact OWAL so we can check into it.')
      })
    },
  },
}
</script>

<style scope>
#selected {
  font-weight: bold;
  color: green;
}
.owal-warn {
  font-weight: bold;
  color:darkred;
  font-size: 1.2rem;
}
.owal-model__container {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;
  align-items: center;
  height: 100%;
}

.owal-model__panel  {
  max-width: 45rem;
}

.owal-model__model-desciption {
  align-items: center;
}

.owal-model__available-model-container {
  padding: 1rem 0rem 1rem 0rem;
}

.owal-model__model-label {
  font-weight: bold;
}

.owal-model__secondary-column {
  padding-right: 1rem;
  width: 23%
  /* flex-grow: 1; */
}

.owal-model__label-column {
  /* flex-grow: 4; */
  width: 45%
}
.owal-model__model-card {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding: 0rem 1rem .5rem 1rem;
  border-radius: 5px;
}

.tooltip {
  cursor: pointer;
  padding-right: 1.5rem;
}
/* Tooltip text */
.tooltip .tooltiptext {
  visibility: hidden;
  width: 120px;
  background-color: black;
  color: #fff;
  text-align: center;
  padding: 5px 0;
  border-radius: 6px;
  font-size: 1rem;

  /* Position the tooltip text - see examples below! */
  position: absolute;
  z-index: 1;
}

/* Show the tooltip text when you mouse over the tooltip container */
.tooltip:hover .tooltiptext {
    visibility: visible;
}

</style>
